import type { PaginatedDeviceTag, PaginatedDeviceTagLeveled } from "@/api/resources/deviceTags/types";
import type { DeviceTag } from "@/supabase.types";
import { sortObjectsBy } from "@/utils/format";

export const getDeviceTagsLeveled = (deviceTags: PaginatedDeviceTag[]): PaginatedDeviceTagLeveled[] => {
  const deviceTagsChildrenMap = deviceTags.reduce<
    Record<string, { deviceTag?: PaginatedDeviceTag; children: PaginatedDeviceTag[] }>
  >((reduceObj, deviceTag) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!reduceObj[deviceTag.id]) {
      reduceObj[deviceTag.id] = { deviceTag, children: [] };
    } else reduceObj[deviceTag.id].deviceTag ??= deviceTag;
    if (deviceTag.parent_device_tag_id) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!reduceObj[deviceTag.parent_device_tag_id]) {
        reduceObj[deviceTag.parent_device_tag_id] = { deviceTag: undefined, children: [] };
      }
      reduceObj[deviceTag.parent_device_tag_id].children = [
        ...reduceObj[deviceTag.parent_device_tag_id].children,
        deviceTag,
      ];
    }
    return reduceObj;
  }, {});

  const getDeviceTagChildren = ({
    children,
    level,
  }: {
    children: PaginatedDeviceTag[];
    level: number;
  }): PaginatedDeviceTagLeveled[] | undefined => {
    if (children.length <= 0) return undefined;
    return children.map((child) => {
      const newLevel = level + 1;
      const grandChildren = getDeviceTagChildren({
        children: deviceTagsChildrenMap[child.id].children,
        level: newLevel,
      });
      return {
        ...child,
        value: child.id,
        label: child.name,
        level: newLevel,
        children: grandChildren,
      };
    });
  };

  const finalDeviceTagTree = [];
  for (const device_tag_id in deviceTagsChildrenMap) {
    if (deviceTagsChildrenMap[device_tag_id].deviceTag?.parent_device_tag_id === null) {
      const level = 1;
      const children = getDeviceTagChildren({ children: deviceTagsChildrenMap[device_tag_id].children, level });
      const tag = deviceTagsChildrenMap[device_tag_id].deviceTag;
      finalDeviceTagTree.push({ ...tag, value: tag.id, label: tag.name, level, children });
    }
  }

  return [...finalDeviceTagTree].sort(sortObjectsBy("name"));
};

export const getDeviceTagsLeveledFlattened = (
  deviceTagsLevel: PaginatedDeviceTagLeveled[],
): PaginatedDeviceTagLeveled[] => {
  let flattenedLeveledDeviceTags: PaginatedDeviceTagLeveled[] = [...deviceTagsLevel];
  while (flattenedLeveledDeviceTags.some((deviceTag) => deviceTag.children)) {
    flattenedLeveledDeviceTags = flattenedLeveledDeviceTags
      .flatMap((deviceTag) => {
        const { children, ...deviceTagRaw } = deviceTag;
        return deviceTag.children
          ? [
              deviceTagRaw,
              deviceTag.children.map((deviceTagChild) => ({
                ...deviceTagChild,
                children: deviceTagChild.children,
              })),
            ]
          : deviceTag;
      })
      .flat();
  }
  return flattenedLeveledDeviceTags;
};

export const getDeviceTagChildren = (id: string, deviceTags: DeviceTag[]): string[] => {
  const childIds: string[] = [id];
  const directChildren = deviceTags.filter((element) => element.parent_device_tag_id === id);
  directChildren.forEach((child) => {
    childIds.push(child.id);
    childIds.push(...getDeviceTagChildren(child.id, deviceTags));
  });

  return childIds;
};

export const getAllDeviceTagChildren = (ids: string[], deviceTags: DeviceTag[]): string[] => {
  return ids.flatMap((id) => getDeviceTagChildren(id, deviceTags));
};

export const getDeviceTagParents = (elementId: string, deviceTags: DeviceTag[]): DeviceTag[] => {
  const parentChain: DeviceTag[] = [];

  function findParents(currentId: string) {
    const currentElement = deviceTags.find((deviceTag) => deviceTag.id === currentId);
    if (currentElement) {
      parentChain.push(currentElement);
      if (currentElement.parent_device_tag_id) {
        findParents(currentElement.parent_device_tag_id);
      }
    }
  }
  findParents(elementId);
  return parentChain.reverse();
};
